import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

// Authentication layout components
import BasicLayout from "pages/authentication/components/BasicLayout";
import curved6 from "assets/images/curved-images/curved14.jpeg";
import AuthApi from "../../../api/auth";
import PermissionsApi from "../../../api/permissions";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function SignUp() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [identificacion, setIdentificacion] = useState("");
  const [nombres, setNombres] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [ciudadDoc, setCiudadDoc] = useState("");
  //const [firstName, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [esOperador, setEsOperador] = useState(false);
  const [tarjetaProfesional, setTarjetaProfesional] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [fechaNacimiento, setFechaNacimiento] = useState(null);
  const [genero, setGenero] = useState("");
  const [buttonText, setButtonText] = useState("REGISTRAR");
  const [error, setError] = useState(undefined);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await PermissionsApi.fetchGroups();
        setGrupos(response.data);
      } catch (error) {
        console.error("Error fetching groups:", error);
        setError("Error al cargar los grupos");
      }
    };
    fetchGroups();
  }, []);

  const handleSetEsOperador = () => {
    setEsOperador(!esOperador);
    if (!esOperador) {
      const operadorGroup = grupos.find((g) => g.name === "OPERADOR(A)");
      if (operadorGroup) {
        setSelectedRoles([operadorGroup.id]);
      }
    } else {
      setSelectedRoles([]);
    }
  };

  const handleRoleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedRoles(typeof value === "string" ? value.split(",") : value);
  };

  const validateForm = () => {
    if (identificacion === "") return "Debes ingresar una Identificación.";
    if (nombres === "") return "Debes ingresar los Nombres.";
    if (apellidos === "") return "Debes ingresar los Apellidos.";
    if (email === "") return "Debes ingresar un Correo Electrónico.";
    if (password === "") return "Debes ingresar una Contraseña.";
    if (esOperador && tarjetaProfesional === "") return "Debes ingresar la Tarjeta Profesional.";
    if (selectedRoles.length === 0) return "Debes seleccionar al menos un rol.";
    if (!fechaNacimiento) return "Debes seleccionar una Fecha de Nacimiento.";
    if (genero === "") return "Debes seleccionar un Género.";
    return null;
  };

  const clearForm = () => {
    setIdentificacion("");
    setNombres("");
    setApellidos("");
    setEmail("");
    setPassword("");
    setEsOperador(false);
    setTarjetaProfesional("");
    setSelectedRoles([]);
    setFechaNacimiento(null);
    setGenero("");
    setError(undefined);
  };

  const register = async (event) => {
    if (event) {
      event.preventDefault();
    }

    // Validar formulario
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setButtonText("Registrando...");
    setError(undefined);

    try {
      const userData = {
        identificacion,
        email,
        password,
        nombres,
        apellidos,
        ciudad_documento: ciudadDoc,
        es_operador: esOperador,
        groups: selectedRoles,
        fecha_nacimiento: fechaNacimiento
          ? dayjs(fechaNacimiento).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
          : null,
        genero,
        ...(esOperador && { tarjeta_profesional: tarjetaProfesional }),
      };

      const response = await AuthApi.Register(userData);

      if (response.data.success) {
        setButtonText("¡Registro exitoso!");
        clearForm();
        // Opcional: redirigir después del registro exitoso
        setTimeout(() => {
          history.push("/admin/usuarios");
        }, 2000);
      } else {
        setError(response.data.error || "Error en el registro");
        setButtonText("REGISTRAR");
      }
    } catch (error) {
      console.error("Error en registro:", error);
      setError(typeof error.message === "string" ? error.message : "Error al registrar el usuario");
      setButtonText("REGISTRAR");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BasicLayout
      title="Creación de Usuario"
      description="Utiliza esta opción para registrar nuevos usuarios"
      image={curved6}
    >
      <Card>
        <SuiBox pt={2} pb={3} px={3}>
          <SuiBox mb={2} textAlign="center">
            <SuiTypography variant="h6" fontWeight="medium">
              Utiliza esta opción para registrar nuevos usuarios
            </SuiTypography>
          </SuiBox>
          <SuiBox component="form" role="form">
            <SuiBox mb={2}>
              <SuiInput
                value={identificacion}
                onChange={(event) => {
                  const onlyNums = event.target.value.replace(/[^0-9]/g, "");
                  setIdentificacion(onlyNums);
                  setError(undefined);
                }}
                type="text"
                placeholder="Identificación"
                disabled={isLoading}
                inputProps={{
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  style: { WebkitAppearance: "none", MozAppearance: "textfield" },
                }}
              />
            </SuiBox>
            <SuiBox mb={2}>
              <SuiInput
                value={nombres}
                onChange={(event) => {
                  setNombres(event.target.value);
                  setError(undefined);
                }}
                placeholder="Nombres"
                disabled={isLoading}
              />
            </SuiBox>
            <SuiBox mb={2}>
              <SuiInput
                value={apellidos}
                onChange={(event) => {
                  setApellidos(event.target.value);
                  setError(undefined);
                }}
                placeholder="Apellidos"
                disabled={isLoading}
              />
            </SuiBox>
            <SuiBox mb={2}>
              <SuiInput
                value={ciudadDoc}
                onChange={(event) => {
                  setCiudadDoc(event.target.value);
                  setError(undefined);
                }}
                placeholder="Ciudad Expedición Cédula"
              />
            </SuiBox>
            <SuiBox mb={2}>
              <FormControl fullWidth>
                <InputLabel
                  id="genero-label"
                  sx={{
                    fontSize: "0.875rem",
                    textAlign: "center",
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                  }}
                >
                  Género
                </InputLabel>
                <Select
                  labelId="genero-label"
                  id="genero-select"
                  value={genero}
                  label="Género"
                  onChange={(event) => setGenero(event.target.value)}
                  disabled={isLoading}
                >
                  <MenuItem value="FE">Femenino</MenuItem>
                  <MenuItem value="MA">Masculino</MenuItem>
                  <MenuItem value="NR">No Responde</MenuItem>
                </Select>
              </FormControl>
            </SuiBox>
            <SuiBox mb={2}>
              <SuiInput
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError(undefined);
                }}
                type="email"
                placeholder="Correo Electrónico"
                disabled={isLoading}
              />
            </SuiBox>
            <SuiBox mb={2}>
              <SuiInput
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError(undefined);
                }}
                type="password"
                placeholder="Contraseña"
                disabled={isLoading}
              />
            </SuiBox>
            <SuiBox mb={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={fechaNacimiento}
                  onChange={(newValue) => {
                    setFechaNacimiento(newValue);
                    setError(undefined);
                  }}
                  format="DD/MM/YYYY"
                  disabled={isLoading}
                  renderInput={() => (
                    <SuiInput
                      placeholder="Fecha de Nacimiento"
                      icon={{
                        component: <CalendarTodayIcon />,
                        direction: "right",
                      }}
                      customClass="custom-date-picker-input"
                      disabled={isLoading}
                    />
                  )}
                />
              </LocalizationProvider>
            </SuiBox>
            <SuiBox display="flex" alignItems="center" mb={2}>
              <Checkbox checked={esOperador} onChange={handleSetEsOperador} disabled={isLoading} />
              <SuiTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSetEsOperador}
                customClass="cursor-pointer user-select-none"
              >
                Es Operador
              </SuiTypography>
            </SuiBox>
            {esOperador && (
              <SuiBox mb={2}>
                <SuiInput
                  value={tarjetaProfesional}
                  onChange={(event) => {
                    setTarjetaProfesional(event.target.value);
                    setError(undefined);
                  }}
                  placeholder="Tarjeta Profesional"
                  disabled={isLoading}
                />
              </SuiBox>
            )}
            <FormControl sx={{ width: "100%", mb: 2 }}>
              <InputLabel
                id="multiple-checkbox-label"
                sx={{
                  bgcolor: "white",
                  px: 1,
                  fontSize: "0.875rem",
                }}
              >
                Roles
              </InputLabel>
              <Select
                labelId="multiple-checkbox-label"
                id="multiple-checkbox"
                multiple
                value={selectedRoles}
                onChange={handleRoleChange}
                input={<OutlinedInput label="Roles" sx={{ height: "41px" }} />}
                renderValue={(selected) =>
                  grupos
                    .filter((g) => selected.includes(g.id))
                    .map((g) => g.name)
                    .join(", ")
                }
                MenuProps={MenuProps}
                disabled={isLoading}
                sx={{
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    fontSize: "0.875rem",
                    minHeight: "1.4375em",
                    padding: "0.5rem",
                  },
                }}
              >
                {grupos.map((grupo) => (
                  <MenuItem
                    key={grupo.id}
                    value={grupo.id}
                    sx={{
                      padding: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Checkbox
                      checked={selectedRoles.indexOf(grupo.id) > -1}
                      sx={{
                        padding: 0,
                        marginRight: "0.5rem",
                      }}
                    />
                    <ListItemText
                      primary={grupo.name}
                      primaryTypographyProps={{
                        style: {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <SuiBox mt={2} mb={2} textAlign="center">
              <h6
                style={{
                  fontSize: ".8em",
                  color: "red",
                  textAlign: "center",
                  fontWeight: 400,
                  transition: ".2s all",
                }}
              >
                {error}
              </h6>
            </SuiBox>
            <SuiBox mt={4} mb={1}>
              <SuiButton
                onClick={register}
                variant="gradient"
                buttonColor="dark"
                fullWidth
                disabled={isLoading}
              >
                {buttonText}
              </SuiButton>
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </Card>
    </BasicLayout>
  );
}

export default SignUp;
